import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { ScrollReveal } from '@/components/ScrollReveal';
import BackToTop from '@/components/BackToTop';
import StickyReservationCTA from '@/components/StickyReservationCTA';
import { Bike, Zap, Baby, Truck, ArrowRight, MapPin, Info } from 'lucide-react';

const ChoisirVelo = () => {
  const bikeTypes = [
    {
      id: 'classique',
      title: 'Vélo classique adulte',
      icon: Bike,
      description: 'Vélo de ville confortable, idéal pour les balades en famille et les déplacements quotidiens.',
      features: ['Confortable', 'Fiable', 'Facile à utiliser'],
      color: 'bg-blue-100 text-blue-700 border-blue-200'
    },
    {
      id: 'electrique',
      title: 'Vélo électrique',
      icon: Zap,
      description: 'Assistance électrique pour parcourir plus de distance sans effort, parfait pour découvrir la région.',
      features: ['Assistance électrique', 'Longue autonomie', 'Moins d\'effort'],
      color: 'bg-amber-100 text-amber-700 border-amber-200'
    },
    {
      id: 'enfant',
      title: 'Vélo enfant',
      icon: Baby,
      description: 'Vélos adaptés aux enfants, différentes tailles disponibles selon l\'âge et la morphologie.',
      features: ['Sécurisé', 'Différentes tailles', 'Adapté aux enfants'],
      color: 'bg-green-100 text-green-700 border-green-200'
    },
    {
      id: 'specifique',
      title: 'Vélos spécifiques',
      icon: Truck,
      description: 'Vélos cargo, suiveurs, tandems et autres modèles spéciaux selon les campings partenaires.',
      features: ['Cargo', 'Suiveur', 'Tandem'],
      color: 'bg-purple-100 text-purple-700 border-purple-200'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary to-primary/90 text-primary-foreground py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <div className="w-16 h-16 bg-primary-foreground/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Bike className="h-8 w-8" />
            </div>
            <h1 className="text-3xl lg:text-5xl font-bold mb-6">
              Choisir mon vélo
            </h1>
            <p className="text-lg lg:text-xl text-primary-foreground/90 leading-relaxed max-w-3xl mx-auto">
              Nos vélos sont pensés pour un usage en vacances, fiables et entretenus par des professionnels.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Types de vélos */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {bikeTypes.map((bike, index) => (
              <ScrollReveal key={bike.id} delay={index * 100}>
                <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                  <CardContent className="p-6 lg:p-8">
                    <div className="flex flex-col h-full">
                      {/* Icône et titre */}
                      <div className="flex items-center mb-4">
                        <div className={`p-3 rounded-xl ${bike.color} mr-4`}>
                          <bike.icon className="h-6 w-6" />
                        </div>
                        <h3 className="text-xl lg:text-2xl font-bold text-slate-900">
                          {bike.title}
                        </h3>
                      </div>
                      
                      {/* Description */}
                      <p className="text-slate-700 leading-relaxed mb-6 flex-grow">
                        {bike.description}
                      </p>
                      
                      {/* Caractéristiques */}
                      <div className="mb-6">
                        <div className="flex flex-wrap gap-2">
                          {bike.features.map((feature, featureIndex) => (
                            <span 
                              key={featureIndex}
                              className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-medium"
                            >
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      {/* Bouton */}
                      <Button 
                        asChild 
                        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3"
                      >
                        <Link to="/reserver">
                          Voir la disponibilité
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Message pédagogique */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg border border-slate-200">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Info className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
                <div className="flex-grow">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Important à savoir
                  </h3>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    La disponibilité des vélos dépend du camping sélectionné. 
                    Chaque établissement partenaire dispose d'une flotte adaptée à sa clientèle.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button asChild variant="outline" size="sm">
                      <Link to="/reserver">
                        <MapPin className="mr-2 h-4 w-4" />
                        Choisir mon camping
                      </Link>
                    </Button>
                    <Button asChild variant="ghost" size="sm">
                      <Link to="/faq">
                        Questions fréquentes
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4">
              Prêt à réserver votre vélo ?
            </h2>
            <p className="text-lg text-primary-foreground/90 mb-8 max-w-2xl mx-auto">
              Sélectionnez votre camping ou hébergement pour accéder directement à la réservation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                size="lg" 
                variant="secondary" 
                className="bg-white text-primary hover:bg-white/90 font-semibold px-8"
              >
                <Link to="/reserver">
                  <MapPin className="mr-2 h-5 w-5" />
                  Choisir mon camping
                </Link>
              </Button>
              <Button 
                asChild 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10 font-semibold px-8"
              >
                <Link to="/nos-services">
                  Découvrir nos services
                </Link>
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <StickyReservationCTA />
      <BackToTop />
      <Footer />
    </div>
  );
};

export default ChoisirVelo;